import { App, Button, Flex, Modal, Skeleton, Typography } from "antd"
import dayjs from "dayjs"
import React, { Suspense, lazy, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../hooks/use-app-selector.ts"
import { errorSave } from "../../../../../redux/errors.slice.ts"
import { DATE_FORMATS_OBJ } from "../../../../TeamManagementPage/constants/date-formats.ts"
import { showNotification } from "../../../../../utils/notification/show-notification.ts"
import { useActivityEvent } from "../../../../Leads/components/ActivityQuickDetails/useActivityEvent.ts"
import * as services from "../../../api/services.ts"
import { attendeeCancellationNotice } from "../../../helpers/attendee-cancellation-notice.ts"
import { calendarEditAllowedFromPermissions } from "../../../helpers/calendar-permissions.selectors.ts"
import { type CalendarPermissions, type Event } from "../../../types/types.ts"
import { setStoredEventColor } from "../../helpers/event-custom-colors.ts"

const loadCalendarView = () => import("./CalendarView.tsx")
const CalendarView = lazy(loadCalendarView)

const { Text } = Typography

interface StandaloneEventEditModalProps {
  eventId: string
  /** Fired after a successful save or delete, so the caller re-reads
   *  whatever record (a lead, a table row) points at this event. */
  onSaved: () => void
  onClose: () => void
}

/** The chrome for every state before `CalendarView` itself is on screen —
 *  the initial fetch, its error/retry, and the Suspense fallback while the
 *  page's own chunk loads. One shell so the two call sites can't drift. */
function LoadingShell({
  onClose,
  children
}: {
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <Modal
      open
      footer={null}
      maskClosable={false}
      centered
      width={440}
      onCancel={onClose}
      data-testid={"standalone-event-editor-loading"}
    >
      {children}
    </Modal>
  )
}

/**
 * The exact `CalendarView` edit experience the Calendar page itself renders
 * — mounted directly on top of *any* page, reading and writing the event
 * through the plain API rather than the Calendar page's own Redux slice, so
 * opening it never navigates the caller away from where they already are.
 *
 * `docs/kb/decisions/2026-09-12-appointment-edit-opens-the-calendar-view.md`
 * reached "the same view everywhere" by deep-linking to the Calendar page;
 * this reaches it by mounting the same component in place instead — the
 * Lead/Project workspace header and the Leads Active table's own appointment
 * and follow-up Edit actions all use this rather than `navigate(...)`.
 */
export function StandaloneEventEditModal({
  eventId,
  onSaved,
  onClose
}: StandaloneEventEditModalProps) {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const dispatch = useAppDispatch()
  // `pollOnFocus: false` — the tab-focus/30s background poll `useActivityEvent`
  // otherwise arms exists for a passive read-only Quick View catching an
  // out-of-band RSVP answer; it buys an actively-editing caller nothing but
  // a wasted request (antd's own `Form` only ever consumes `initialValues`
  // on first mount, so a background refetch can't silently clobber in-
  // progress typing either way, but there's still no reason to fetch it).
  const { event, loading, error, refetch } = useActivityEvent(
    eventId,
    true,
    false
  )
  const [saving, setSaving] = useState(false)

  // The Calendar page's own chunk may not be cached yet if the caller never
  // visited it this session — warmed as soon as this modal mounts, in
  // parallel with the event fetch above, rather than only once `loading`
  // clears and `<CalendarView>` is first rendered (its own `lazy()` factory
  // is otherwise not invoked until React attempts that render).
  useEffect(() => {
    void loadCalendarView()
  }, [])

  const permissionCodes = useAppSelector((state) => state.auth?.permissions)
  const userInfo = useAppSelector((state) => state.auth?.userInfo)
  const dateFormat = userInfo?.date_format
    ? (DATE_FORMATS_OBJ?.[userInfo.date_format]?.label ?? null)
    : null
  // Same fallback `reschedule-dropped-event.ts` and `time.ts` use — a real
  // user session always has both by the time any authenticated page can
  // render, but this modal makes no boot-order assumption of its own.
  const timezone = userInfo?.timezone || dayjs.tz.guess()
  const userId = userInfo?.id || ""

  const permissions: CalendarPermissions = {
    delete: !!permissionCodes?.codes?.calendar?.calendar?.calendar_delete,
    edit: calendarEditAllowedFromPermissions(permissionCodes),
    viewLead: !!permissionCodes?.codes?.leads?.leads?.lead_view,
    viewProject: !!permissionCodes?.codes?.projects?.projects?.project_view
  }

  const isReminder = event?.type?.toLowerCase() === "reminder"

  // Mirrors calendar.thunks.ts's partialUpdateCalendarEvent exactly (minus
  // the Calendar slice's own list/selection bookkeeping, which nothing here
  // reads), so a save through this modal looks identical to one made from
  // the Calendar page itself — same conflict warning, same reminder-aware
  // success toast. `saving` blocks a second submit while the first is still
  // in flight — CalendarView's own Save button reads it as its `loading`
  // prop, same as the Calendar page's Redux-backed `saveFetching`.
  const handleSave = useCallback(
    async (values: Event) => {
      const { custom_color, ...payload } = values as Event & {
        custom_color?: string | null
      }
      setSaving(true)
      try {
        const response = await services.partialUpdateEvent({
          ...payload,
          id: eventId
        })

        if (!response.ok) {
          dispatch(
            errorSave((response.data as { details?: unknown })?.details)
          )
          // The form places this one under its own checkbox.
          const fieldErrors = (
            response.data as { details?: { field_errors?: Record<string, unknown> } }
          )?.details?.field_errors
          if (fieldErrors?.share_project_education) throw response.data
          return
        }

        setStoredEventColor(eventId, custom_color)

        const conflictWarning = (
          response.data as {
            details?: { data?: { conflict_warning?: boolean } }
          }
        )?.details?.data?.conflict_warning
        if (conflictWarning) {
          showNotification.warning({
            message: "Schedule conflict",
            description:
              "One or more participants already have another scheduled event at the selected time."
          })
        }

        showNotification.success({
          message: isReminder
            ? "Reminder updated successfully."
            : "Event updated successfully."
        })
        onSaved()
        onClose()
      } finally {
        setSaving(false)
      }
    },
    [dispatch, eventId, isReminder, onClose, onSaved]
  )

  const handleDelete = useCallback(() => {
    const cancellationNotice = attendeeCancellationNotice(event)
    // Modal.confirm only keeps itself open while its `onOk` promise is
    // pending or rejected — resolving normally on failure (the way a bare
    // early `return` does) reads to antd as success and closes the
    // confirmation as if the delete had gone through. The trailing
    // `.then(() => {})` on the instance antd hands back isn't dead code: it
    // flips antd's own internal `isSilent` flag (see `ActionButton`/
    // `useModal` in antd's source, and the antd issue its own code comments
    // reference) so the rejection below is consumed as "keep the dialog
    // open" without also re-throwing as a genuine unhandled rejection.
    modal
      .confirm({
        centered: true,
        title: t("Confirm deleting"),
        content: [
          t(
            `Are you sure you want to delete this ${isReminder ? "reminder" : "event"}? This action can NOT be undone.`
          ),
          cancellationNotice && t(cancellationNotice)
        ]
          .filter(Boolean)
          .join(" "),
        cancelText: t("Cancel"),
        okText: t("Delete"),
        okButtonProps: { danger: true, ghost: true },
        async onOk() {
          setSaving(true)
          try {
            const response = await services.deleteEvent({ id: eventId })
            if (!response.ok) {
              dispatch(
                errorSave((response.data as { details?: unknown })?.details)
              )
              throw new Error("Failed to delete event")
            }
            showNotification.success({
              message: isReminder
                ? "Reminder deleted successfully."
                : "Event deleted successfully."
            })
            onSaved()
            onClose()
          } finally {
            setSaving(false)
          }
        }
      })
      .then(
        () => {},
        () => {}
      )
  }, [dispatch, event, eventId, isReminder, modal, onClose, onSaved, t])

  // Mirrors calendar.thunks.ts's updateReminderStatus, which is itself just
  // a partialUpdateEvent call with `is_completed` in the payload — a plain
  // no-op default here (rather than this) left a reminder's Mark as
  // Completed/Uncompleted button doing nothing.
  const handleDone = useCallback(
    async (doneEvent: Event, is_completed: boolean) => {
      setSaving(true)
      try {
        const response = await services.partialUpdateEvent({
          id: eventId,
          type: doneEvent.type,
          is_completed
        })
        if (!response.ok) {
          dispatch(
            errorSave((response.data as { details?: unknown })?.details)
          )
          return
        }
        showNotification.success({
          message: is_completed
            ? "Reminder marked as completed."
            : "Reminder marked as uncompleted."
        })
        onSaved()
        onClose()
      } finally {
        setSaving(false)
      }
    },
    [dispatch, eventId, onClose, onSaved]
  )

  // `!event` also covers the render before useActivityEvent's own effect has
  // fired at all — its `loading` starts false, not true, until then.
  if (loading || error || !event) {
    return (
      <LoadingShell onClose={onClose}>
        {!error ? (
          <Skeleton active title paragraph={{ rows: 6 }} />
        ) : (
          <Flex vertical align={"center"} gap={16} className={"py-6"}>
            <Text type={"danger"}>
              {t("standaloneEventEditor.loadFailed")}
            </Text>
            <Button type={"primary"} onClick={refetch}>
              {t("standaloneEventEditor.tryAgain")}
            </Button>
          </Flex>
        )}
      </LoadingShell>
    )
  }

  return (
    <Suspense
      fallback={
        <LoadingShell onClose={onClose}>
          <Skeleton active title paragraph={{ rows: 6 }} />
        </LoadingShell>
      }
    >
      <CalendarView
        // `CalendarView` computes its own `custom_color` from `event.id` via
        // the same `getStoredEventColorHex` helper internally — passing one
        // in here as well would be redundant and would need a field `Event`
        // doesn't declare, forcing an `as unknown` cast past the type
        // checker for no benefit.
        key={event.id}
        dateFormat={dateFormat}
        permissions={permissions}
        saveFetching={saving}
        event={event}
        timezone={timezone}
        userId={userId}
        startInEditMode
        onClose={onClose}
        // No `onEditCancelled` — Cancel falls back to CalendarView's own
        // read view, which is where CalendarViewReminder's Mark as
        // Completed/Uncompleted button (`onDone` below) lives; forcing
        // Cancel to close outright would make it unreachable.
        onDone={handleDone}
        onDelete={handleDelete}
        onSave={handleSave}
        onInvited={refetch}
      />
    </Suspense>
  )
}
